import { ChevronLeft, ChevronRight } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { typography } from '../../styles/typography';

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const toDateKey = date => {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const parseDateKey = key => {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const startOfMonth = date => new Date(date.getFullYear(), date.getMonth(), 1);

const buildCalendarDays = viewMonth => {
  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startOffset = firstDay.getDay();
  const days = [];

  for (let i = 0; i < startOffset; i += 1) {
    const date = new Date(year, month, -startOffset + i + 1);
    days.push({ date, inMonth: false });
  }
  for (let day = 1; day <= lastDay.getDate(); day += 1) {
    days.push({ date: new Date(year, month, day), inMonth: true });
  }
  while (days.length % 7 !== 0 || days.length < 35) {
    const last = days[days.length - 1].date;
    const next = new Date(last);
    next.setDate(last.getDate() + 1);
    days.push({ date: next, inMonth: false });
    if (days.length >= 42) break;
  }
  return days;
};

const CleaningScheduleDatePicker = ({
  isOpen,
  onClose,
  onConfirm,
  initialSelectedDates = [],
  assignedDates = [],
  title = 'Select dates for community shift',
  subtitle = 'Pick date or select multiple dates',
}) => {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const todayKey = useMemo(() => toDateKey(today), [today]);

  const [viewMonth, setViewMonth] = useState(() => startOfMonth(new Date()));
  const [selectedKeys, setSelectedKeys] = useState(new Set());

  const assignedSet = useMemo(
    () => new Set((assignedDates || []).map(toDateKey).filter(Boolean)),
    [assignedDates]
  );

  useEffect(() => {
    if (!isOpen) return;
    const keys = (initialSelectedDates || [])
      .map(d => toDateKey(d))
      .filter(Boolean);
    if (keys.length === 0) {
      keys.push(todayKey);
    }
    setSelectedKeys(new Set(keys));
    const anchor = keys.length > 0 ? parseDateKey(keys[0]) : today;
    setViewMonth(startOfMonth(anchor));
  }, [isOpen, initialSelectedDates, today, todayKey]);

  const calendarDays = useMemo(() => buildCalendarDays(viewMonth), [viewMonth]);

  const toggleDate = date => {
    if (date < today) return;
    const key = toDateKey(date);
    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleConfirm = () => {
    const selectedDates = Array.from(selectedKeys)
      .map(parseDateKey)
      .sort((a, b) => a - b);

    if (selectedDates.length === 0) {
      return;
    }

    onConfirm({
      mode: 'multiple',
      selectedDates,
      singleDate: selectedDates.length === 1 ? selectedDates[0] : null,
      startDate: selectedDates[0],
      endDate: selectedDates[selectedDates.length - 1],
    });
    onClose();
  };

  const goPrevMonth = () => {
    setViewMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const goNextMonth = () => {
    setViewMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  if (!isOpen) return null;

  const monthLabel = `${MONTH_NAMES[viewMonth.getMonth()]} ${viewMonth.getFullYear()}`;
  const selectedCount = selectedKeys.size;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="flex w-full max-w-md max-h-[90vh] flex-col overflow-hidden rounded-lg bg-white shadow-lg">
        {/* Header — matches shift datepicker modal */}
        <div className="flex shrink-0 items-start justify-between border-b border-gray-100 px-6 pt-6 pb-4">
          <div>
            <h3 className={`${typography.h5} text-gray-800 font-semibold`}>{title}</h3>
            <p className={`${typography.p} text-gray-600 mt-1`}>{subtitle}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 shrink-0 ml-3"
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Legend — matches blue-50 sections on Schedule a community shift */}
          <div className="mb-4 rounded-xl bg-blue-50 px-4 py-3">
            <div className="flex flex-wrap items-center gap-5">
              <div className="flex items-center gap-2">
                <span className="h-4 w-4 rounded bg-blue-200 border border-blue-300" />
                <span className={`${typography.p} text-gray-700 text-sm`}>Assigned</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="h-4 w-4 rounded bg-green-600" />
                <span className={`${typography.p} text-gray-700 text-sm`}>Selected</span>
              </div>
            </div>
          </div>

          {/* Month navigation — matches ScheduleCalendar */}
          <div className="mb-3 flex items-center justify-between">
            <button
              type="button"
              onClick={goPrevMonth}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Previous month"
            >
              <ChevronLeft className="w-5 h-5 text-gray-700" />
            </button>
            <h4 className={`${typography.h4} text-gray-900 font-semibold`}>{monthLabel}</h4>
            <button
              type="button"
              onClick={goNextMonth}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Next month"
            >
              <ChevronRight className="w-5 h-5 text-gray-700" />
            </button>
          </div>

          {/* Day headers */}
          <div className="mb-2 grid grid-cols-7 gap-1">
            {WEEKDAYS.map((day, index) => (
              <div
                key={`${day}-${index}`}
                className="text-center text-sm font-semibold text-gray-700 py-1"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map(({ date, inMonth }) => {
              const key = toDateKey(date);
              const isPast = date < today;
              const isAssigned = assignedSet.has(key);
              const isSelected = selectedKeys.has(key);
              const isToday = key === todayKey;

              let cellClass =
                'aspect-square flex items-center justify-center text-sm rounded-lg transition-all';

              if (!inMonth) {
                cellClass += ' text-gray-300 cursor-default';
              } else if (isPast) {
                cellClass += ' text-gray-300 cursor-not-allowed';
              } else if (isSelected) {
                cellClass += ' bg-green-600 text-white font-semibold shadow-md cursor-pointer';
              } else if (isAssigned) {
                cellClass += ' bg-blue-100 text-blue-800 font-medium hover:bg-blue-200 cursor-pointer';
              } else if (isToday) {
                cellClass += ' bg-blue-50 text-blue-900 border-2 border-blue-300 hover:bg-blue-100 cursor-pointer';
              } else {
                cellClass += ' text-gray-700 hover:bg-gray-100 cursor-pointer';
              }

              return (
                <button
                  key={`${key}-${inMonth}`}
                  type="button"
                  disabled={!inMonth || isPast}
                  onClick={() => inMonth && !isPast && toggleDate(date)}
                  className={cellClass}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>

          {selectedCount > 0 && (
            <p className={`${typography.p} text-gray-600 mt-4 text-sm`}>
              {selectedCount} date{selectedCount === 1 ? '' : 's'} selected
            </p>
          )}
        </div>

        {/* Footer — matches shift datepicker Cancel / Confirm */}
        <div className="flex shrink-0 justify-end gap-3 border-t border-gray-100 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-600 rounded-md hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={selectedCount === 0}
            className="px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

export default CleaningScheduleDatePicker;
